// @ts-nocheck
'use client'

import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  styled,
  CircularProgress,
} from '@mui/material'
import dayjs from 'dayjs'
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-premium'

import Filters from './Filters'
import AdditionalActionsSendBar, { type MatchingRows } from './AdditionalActionsSendBar'
import MoreFiltersDialog, { EMPTY_FILTERS, MoreFilters } from '@/components/Notifications/MoreFiltersDialog'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import GoToWaterTreeButton from '@/components/Analytics/GoToWaterTreeButton'
import { useMeterTreeState } from '@/components/Analytics/useMeterTreeState'

import columnsDaily from '@/components/features/config/columns/notifications/daily.column.config'
import columnsHourly from '@/components/features/config/columns/notifications/hourly.column.config'
import columnsShared from '@/components/features/config/columns/analytics/analytics.details.column.config'
import columnsActions from '@/components/features/config/columns/notifications/actions.column.config'
import columnsNotifications from '@/components/features/config/columns/notifications/notification.column.config'

import { getAlertTypes, getAllConsumerNotifications } from '@/services/notificationService'
import {
  buildAlertsListParams,
  createDefaultAlertsListFilters,
  mapAlertsListRow,
} from './alertsListFilters'
import {
  detailsTabEmptyLabel,
  isSharedTab,
  treeOfTab,
  detailsTabFromIndex,
  detailsTabNeed,
  loadDetailsTabPage,
  resolveDetailsTabNotice,
} from './detailsTabs'
import { getServerPaginationUpdate } from '@/utils/serverPagination'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { useParams } from 'react-router-dom'
import { NO_SUPPLIED_TREE_HINT } from '@/utils/premiseTree'
import { useAppTheme } from '@/theme/ThemeContext'
import { useScopedApiFilters } from '@/store/table/table'

const StyledTypography = styled(Typography)<{ mode?: 'light' | 'dark' }>(({ mode }) => ({
  color: mode === 'dark' ? '#FFFFFF' : '#003F61',
  textAlign: 'right',
  fontFamily: 'Rubik, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
}))
const BLUE = '#72A4EB'

const columnsByTab = {
  daily: columnsDaily,
  hourly: columnsHourly,
  sharedParent: columnsShared,
  sharedChild: columnsShared,
  alerts: columnsNotifications,
  activity: columnsActions,
}

const MemoizedDataGrid = React.memo(DataGridPremiumDemo)

const dashCell = (params: any) => params.value ?? '-'

const rightPanelColumns = [
  { field: 'meterNumber', headerName: 'מספר מד', flex: 1, align: 'center', headerAlign: 'center', renderCell: dashCell },
  { field: 'consumerName', headerName: 'שם הצרכן', flex: 1.2, align: 'center', headerAlign: 'center', renderCell: dashCell },
  { field: 'waterCard', headerName: 'כרטיס מים', flex: 1, align: 'center', headerAlign: 'center', renderCell: dashCell },
]

const Details: React.FC = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { siteId } = useParams()

  const [alertTypes, setAlertTypes] = useState<any[]>([])
  const [alertTypesState, setAlertTypesState] = useState<'loading' | 'failed' | 'ready'>('loading')

  // One object drives the toolbar's initial selection, the page state and the
  // first request, so the grid can never show rows the filters do not describe.
  const defaultFilters = useMemo(createDefaultAlertsListFilters, [])
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [moreFilters, setMoreFilters] = useState<MoreFilters>(EMPTY_FILTERS)

  const [allAlerts, setAllAlerts] = useState<any[]>([])
  const [alertsTotal, setAlertsTotal] = useState(0)
  const [alertsPage, setAlertsPage] = useState(0)
  const [alertsPageSize, setAlertsPageSize] = useState(() => getTablePageSize(50))
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [alertsFailed, setAlertsFailed] = useState(false)

  const [activeTab, setActiveTab] = useState(0)
  const tab = detailsTabFromIndex(activeTab)
  // The tab grid owns the column filters. Each tab starts with none, because its columns differ.
  useScopedApiFilters(`additional-actions:${tab}`)
  const [selectedRow, setSelectedRow] = useState<any>(null)
  // The alerts list carries neither parentId nor supplierOfId, so the shown meter's trees come from
  // the single-consumer endpoint, and only while a shared-consumption tab needs them.
  const meterTrees = useMeterTreeState(siteId, selectedRow?.meterNumber, isSharedTab(tab))
  const sharedTree = treeOfTab(tab, meterTrees)
  const sharedTreeId = sharedTree?.status === 'found' ? sharedTree.treeId : null
  const [tabRows, setTabRows] = useState<any[]>([])
  const [tabTotal, setTabTotal] = useState(0)
  const [tabPage, setTabPage] = useState(0)
  const [tabLoading, setTabLoading] = useState(false)
  const [tabFailed, setTabFailed] = useState(false)

  const [moreOpen, setMoreOpen] = useState(false)

  const [rowSelection, setRowSelection] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  })

  const stableRowSelection = useMemo(() => rowSelection, [rowSelection])
  const alertsRequestRef = useRef(0)

  const fetchAlertsList = useCallback(async (
    filters: any,
    more: MoreFilters,
    paging: { page: number; pageSize: number }
  ) => {
    if (!siteId) return
    const requestId = ++alertsRequestRef.current
    setAlertsLoading(true)
    setAlertsFailed(false)
    try {
      const response = await getAllConsumerNotifications(
        siteId,
        buildAlertsListParams(filters, more, paging)
      )
      if (requestId !== alertsRequestRef.current) return
      if (response.failed) {
        setAllAlerts([])
        setAlertsTotal(0)
        setAlertsFailed(true)
        return
      }
      setAllAlerts((response.result ?? []).map(mapAlertsListRow))
      setAlertsTotal(response.total ?? 0)
    } catch (error) {
      if (requestId !== alertsRequestRef.current) return
      console.error('[Details] Failed to fetch alerts:', error)
      setAllAlerts([])
      setAlertsTotal(0)
      setAlertsFailed(true)
    } finally {
      if (requestId === alertsRequestRef.current) setAlertsLoading(false)
    }
  }, [siteId])

  useEffect(() => {
    if (!siteId) return
    let active = true
    setAlertTypesState('loading')
    getAlertTypes(siteId)
      .then(types => {
        if (!active) return
        setAlertTypes(types ?? [])
        setAlertTypesState('ready')
      })
      .catch(error => {
        if (!active) return
        console.error('[Details] Failed to fetch alert types:', error)
        setAlertTypes([])
        setAlertTypesState('failed')
      })
    return () => {
      active = false
    }
  }, [siteId])

  // The first load sends the defaults the toolbar shows, so the grid and the
  // visible filters always agree.
  useEffect(() => {
    setAppliedFilters(defaultFilters)
    setMoreFilters(EMPTY_FILTERS)
    setAlertsPage(0)
    fetchAlertsList(defaultFilters, EMPTY_FILTERS, {
      page: 0,
      pageSize: alertsPageSize,
    })
    // alertsPageSize is read once here; a page-size change refetches through
    // handleAlertsPageChange, not by restarting the first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilters, fetchAlertsList])

  const clearSelection = useCallback(() => {
    setRowSelection({ type: 'include', ids: new Set<GridRowId>() })
    setSelectedRow(null)
  }, [])

  const handleSearch = useCallback((filters: any) => {
    setAppliedFilters(filters)
    setAlertsPage(0)
    clearSelection()
    fetchAlertsList(filters, moreFilters, { page: 0, pageSize: alertsPageSize })
  }, [clearSelection, fetchAlertsList, moreFilters, alertsPageSize])

  const handleApplyMoreFilters = useCallback((nextFilters: MoreFilters) => {
    setMoreFilters(nextFilters)
    setMoreOpen(false)
    setAlertsPage(0)
    clearSelection()
    fetchAlertsList(appliedFilters, nextFilters, { page: 0, pageSize: alertsPageSize })
  }, [appliedFilters, clearSelection, fetchAlertsList, alertsPageSize])

  const handleAlertsPageChange = useCallback((newPage: number, newPageSize?: number) => {
    const next = getServerPaginationUpdate(
      alertsPageSize,
      newPage,
      newPageSize ?? alertsPageSize
    )
    setAlertsPageSize(next.pageSize)
    setAlertsPage(next.page)
    clearSelection()
    fetchAlertsList(appliedFilters, moreFilters, next)
  }, [alertsPageSize, appliedFilters, clearSelection, fetchAlertsList, moreFilters])

  const selectRow = useCallback((row: any) => {
    setSelectedRow(row ?? null)
    setTabPage(0)
  }, [])

  // The checkboxes pick the recipients of the send bar. Only a row click picks the shown meter.
  const handleRightSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setRowSelection(model)
  }, [])

  const startDate = useMemo(
    () => dayjs(appliedFilters.fromDate ?? dayjs().subtract(2, 'month')).format('YYYY-MM-DD'),
    [appliedFilters.fromDate]
  )
  const endDate = useMemo(
    () => dayjs(appliedFilters.toDate ?? dayjs()).format('YYYY-MM-DD'),
    [appliedFilters.toDate]
  )

  const tabNotice = resolveDetailsTabNotice(
    tab,
    selectedRow
      ? { premiseId: selectedRow.premiseId ?? null, meterNumber: selectedRow.meterNumber ?? null }
      : null,
    tabFailed,
    meterTrees
  )

  const tabRequestRef = useRef(0)

  useEffect(() => {
    const premiseId = selectedRow?.premiseId
    const needsMeter = detailsTabNeed(tab) === 'meter'
    const meterNumber = selectedRow?.meterNumber ?? null
    if (
      !siteId ||
      premiseId == null ||
      (isSharedTab(tab) && sharedTreeId == null) ||
      (needsMeter && (meterNumber == null || meterNumber === ''))
    ) {
      // Discards any answer still in flight for the tab we just left.
      tabRequestRef.current += 1
      setTabRows([])
      setTabTotal(0)
      setTabFailed(false)
      setTabLoading(false)
      return
    }

    const requestId = ++tabRequestRef.current
    setTabLoading(true)
    setTabFailed(false)
    loadDetailsTabPage(tab, {
      siteId,
      premiseId: Number(premiseId),
      meterNumber,
      treeId: sharedTreeId,
      startDate,
      endDate,
      page: tabPage,
      pageSize: alertsPageSize,
    })
      .then(page => {
        if (requestId !== tabRequestRef.current) return
        setTabRows(page.rows)
        setTabTotal(page.total)
      })
      .catch(error => {
        if (requestId !== tabRequestRef.current) return
        console.error(`[Details] Failed to load the ${tab} tab:`, error)
        setTabRows([])
        setTabTotal(0)
        setTabFailed(true)
      })
      .finally(() => {
        if (requestId === tabRequestRef.current) setTabLoading(false)
      })
  }, [siteId, tab, selectedRow, sharedTreeId, startDate, endDate, tabPage, alertsPageSize])

  // One page size drives both grids, so a change in the tab footer reloads the
  // alerts list too instead of leaving that control dead.
  const handleTabPageChange = useCallback((newPage: number, newPageSize?: number) => {
    const next = getServerPaginationUpdate(
      alertsPageSize,
      newPage,
      newPageSize ?? alertsPageSize
    )
    if (next.pageSize !== alertsPageSize) {
      setAlertsPageSize(next.pageSize)
      setAlertsPage(0)
      clearSelection()
      fetchAlertsList(appliedFilters, moreFilters, {
        page: 0,
        pageSize: next.pageSize,
      })
    }
    setTabPage(next.page)
  }, [alertsPageSize, appliedFilters, clearSelection, fetchAlertsList, moreFilters])

  const loadMatchingRows = useCallback(
    async (size: number): Promise<MatchingRows> => {
      const response = await getAllConsumerNotifications(
        siteId,
        buildAlertsListParams(appliedFilters, moreFilters, { page: 0, pageSize: size })
      )
      return response.failed
        ? { rows: [], failed: true }
        : { rows: (response.result ?? []).map(mapAlertsListRow), failed: false }
    },
    [siteId, appliedFilters, moreFilters]
  )

  const noticeSx = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    p: 3,
    textAlign: 'center' as const,
  }

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Top Filters Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            width: '100%',
            pt: 3,
            mb: 2.5,
          }}
        >
          <Filters
            initial={defaultFilters}
            onSearch={handleSearch}
            onMoreFilters={() => setMoreOpen(true)}
            alertTypes={alertTypes}
            alertTypesState={alertTypesState}
          />
        </Box>

        {/* Main Outer Container Frame wrapping ONLY the split tables */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: 2,
            width: '100%',
            alignItems: 'stretch',
            border: `1px solid ${isDarkMode ? '#43597F' : '#E2E8F0'}`,
            borderRadius: 0,
            p: 1.5,
            bgcolor: isDarkMode ? '#091327' : 'transparent',
          }}
        >
          {/* Right Panel: Consumer Alerts Table */}
          <Box
            sx={{
            flex: '0 0 35%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: isDarkMode ? '#1D325A' : '#FFFFFF',
              borderRadius: 0,
              border: `1px solid ${isDarkMode ? '#43597F' : '#E2E8F0'}`,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            {alertsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, minHeight: 400 }}>
                <CircularProgress size={32} sx={{ color: BLUE }} />
              </Box>
            ) : alertsFailed ? (
              <Box dir="rtl" sx={noticeSx}>
                <StyledTypography mode={mode}>
                  רשימת ההתראות לא נטענה. נסו שוב.
                </StyledTypography>
              </Box>
            ) : (
              <MemoizedDataGrid
                width="100%"
                columns={rightPanelColumns}
                rows={allAlerts}
                hideFooter={false}
                headerPosition="bottom"
                checkboxSelection
                rowSelectionModel={stableRowSelection}
                onRowSelectionModelChange={handleRightSelectionChange}
                onRowClick={selectRow}
                shownRowId={selectedRow?.id ?? null}
                pageSize={alertsPageSize}
                paginationMode="server"
                serverSideRowCount={alertsTotal}
                serverSidePage={alertsPage}
                onPaginationChange={handleAlertsPageChange}
                noRowsLabel="אין התראות בטווח התאריכים שנבחר"
                ownsTableStore={false}
                borderRadius={0}
                rowHeight={40}
                columnHeaderHeight={34}
              />
            )}
          </Box>

          {/* Left Panel: Detailed Tab Readings */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: '100%',
            }}
          >
            {/* Tabs Header for Left Table */}
            <Box
              sx={{
                bgcolor: 'transparent',
                border: 'none',
                borderBottom: `1px solid ${isDarkMode ? '#43597F' : '#E2E8F0'}`,
                px: 0,
                pb: 0.5,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, v) => {
                  setActiveTab(v)
                  setTabPage(0)
                }}
                dir="rtl"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    fontFamily: 'Rubik, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: isDarkMode ? 'rgba(187, 215, 255, 0.7)' : '#003F61',
                    '&.Mui-selected': {
                      color: isDarkMode ? '#BBD7FF' : BLUE,
                      fontWeight: 700,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: isDarkMode ? '#BBD7FF' : BLUE,
                    height: 3,
                    borderRadius: '2px',
                  },
                }}
              >
                <Tab label="קריאות יומיות" />
                <Tab label="קריאות שעתיות" />
                <Tab label="צריכה משותפת מול האב" />
                <Tab label="צריכה משותפת מול הבן" />
                <Tab label="התראות" />
                <Tab label="פעילויות" />
              </Tabs>
            </Box>

            {isSharedTab(tab) ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <GoToWaterTreeButton
                  siteId={siteId}
                  state={sharedTree}
                  noTreeHint={tab === 'sharedChild' ? NO_SUPPLIED_TREE_HINT : undefined}
                />
              </Box>
            ) : null}

            {/* Left DataGrid Table */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                bgcolor: isDarkMode ? '#1D325A' : '#FFFFFF',
                borderRadius: 0,
                border: `1px solid ${isDarkMode ? '#43597F' : '#E2E8F0'}`,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {tabNotice ? (
                <Box dir="rtl" sx={noticeSx}>
                  <StyledTypography mode={mode} sx={{ maxWidth: 420 }}>
                    {tabNotice}
                  </StyledTypography>
                </Box>
              ) : (
                <MemoizedDataGrid
                  width="100%"
                  columns={columnsByTab[tab]}
                  rows={tabRows}
                  loading={tabLoading || sharedTree?.status === 'loading'}
                  hideFooter={false}
                  headerPosition="bottom"
                  pageSize={alertsPageSize}
                  paginationMode="server"
                  serverSideRowCount={tabTotal}
                  serverSidePage={tabPage}
                  onPaginationChange={handleTabPageChange}
                  noRowsLabel={detailsTabEmptyLabel(tab)}
                  borderRadius={0}
                  rowHeight={40}
                  autoRowHeight={tab === 'alerts'}
                  twoLineHeaders={tab === 'alerts'}
                  columnHeaderHeight={tab === 'alerts' ? 56 : 34}
                />
              )}
            </Box>
          </Box>
        </Box>

        <AdditionalActionsSendBar
          siteId={siteId}
          rows={allAlerts}
          total={alertsTotal}
          selection={rowSelection}
          loadMatchingRows={loadMatchingRows}
        />
      </Box>

      <MoreFiltersDialog
        open={moreOpen}
        initial={moreFilters}
        onApply={handleApplyMoreFilters}
        onClose={() => setMoreOpen(false)}
        variant="additional-actions"
      />
    </>
  )
}

export default Details
